/**
 * Maps the CSV tables produced by tools/migration (from the previous workshop
 * program's Excel exports) to program inputs. Pure functions; parsing the CSV
 * text happens in the user interface.
 */
import { DomainError } from "./errors";
import { clean, isValidVin, looksLikeCompany, normalizePhone, plateSearchKey, type CustomerInput, type VehicleInput } from "./masterData";

export type Row = Record<string, string | undefined>;

export const LEGACY_CUSTOMER_COLUMNS = ["regi_ugyfelszam", "nev"] as const;
export const LEGACY_VEHICLE_COLUMNS = ["id", "ugyfel_id", "rendszam", "alvazszam", "marka"] as const;
/** Columns of the previous program's work order table (imported in a later step). */
export const LEGACY_WORK_ORDER_COLUMNS = ["munkalapszam", "jarmu_id", "rendszam"] as const;

export type LegacyFileKind = "customers" | "vehicles" | "workOrders" | "workOrderItems" | "workOrderExtras" | "articles";

/** The file names written by tools/migration and the columns each kind needs at least. */
const LEGACY_FILE_COLUMNS: Record<string, readonly string[]> = {
  "ugyfelek.csv": LEGACY_CUSTOMER_COLUMNS,
  "jarmuvek.csv": LEGACY_VEHICLE_COLUMNS,
  "munkalapok.csv": LEGACY_WORK_ORDER_COLUMNS,
  "munkalap_tetelek.csv": ["munkalap_id", "tipus", "megnevezes", "mennyiseg", "brutto_ertek"],
  "munkalap_kiegeszites.csv": ["munkalapszam", "km_ora"],
  "cikktorzs.csv": ["cikkszam", "megnevezes", "utolso_brutto_egysegar"],
};

/**
 * Tells the kind of a table by its columns. When they fit no kind but the file name is one the
 * migration script writes, the error names the missing columns of that kind (#144).
 */
export function detectLegacyFile(headers: readonly string[], fileName?: string): LegacyFileKind {
  try {
    return detectByColumns(headers);
  } catch (err) {
    const base = fileName?.split(/[\\/]/).pop()?.trim().toLowerCase() ?? "";
    const need = LEGACY_FILE_COLUMNS[base];
    if (!need) throw err;
    const h = new Set(headers.map((x) => x.trim()));
    throw new DomainError("IMPORT_COLUMNS_MISSING", need.filter((c) => !h.has(c)).join(", "));
  }
}

function detectByColumns(headers: readonly string[]): LegacyFileKind {
  const h = new Set(headers.map((x) => x.trim()));
  if (LEGACY_CUSTOMER_COLUMNS.every((c) => h.has(c))) return "customers";
  if (LEGACY_WORK_ORDER_COLUMNS.every((c) => h.has(c))) return "workOrders";
  if (h.has("munkalapszam") && h.has("km_ora")) return "workOrderExtras";
  if (["cikkszam", "megnevezes", "utolso_brutto_egysegar"].every((c) => h.has(c)) && !h.has("munkalap_id")) return "articles";
  if (["munkalap_id", "tipus", "megnevezes", "mennyiseg", "brutto_ertek"].every((c) => h.has(c))) return "workOrderItems";
  if (LEGACY_VEHICLE_COLUMNS.every((c) => h.has(c))) return "vehicles";
  throw new DomainError("IMPORT_FILE_UNKNOWN", headers.join(","));
}

export function mapLegacyCustomer(r: Row): CustomerInput | null {
  const name = clean(r.nev);
  const legacyId = clean(r.regi_ugyfelszam)?.replace(/\.0$/, "") ?? null;
  if (!name || !legacyId) return null;
  // Old programs sometimes hold several addresses in one field: the first valid one
  // becomes the email, the others are kept in the notes.
  const emails = (clean(r.email) ?? "").split(/[;,\s]+/).filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  const uniqueEmails = [...new Set(emails)];
  return {
    legacyId,
    name,
    isCompany: looksLikeCompany(name),
    city: clean(r.telepules),
    phone: normalizePhone(r.telefon),
    mobile: normalizePhone(r.mobil),
    email: uniqueEmails[0] ?? null,
    notes: uniqueEmails.length > 1 ? uniqueEmails.slice(1).join("; ") : null,
    contactPerson: clean(r.kepviselo),
    contactPhone: normalizePhone(r.kepviselo_telefon) ?? normalizePhone(r.kepviselo_mobil),
  };
}

export interface LegacyVehicle extends VehicleInput {
  /** Legacy id of the owner, resolved to customerId after the customers are saved. */
  customerLegacyId: string | null;
}

const int = (v: string | undefined): number | null => {
  const s = clean(v);
  if (!s) return null;
  const n = Math.round(Number(s.replace(",", ".")));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function mapLegacyVehicle(r: Row): LegacyVehicle | null {
  const plate = clean(r.rendszam);
  const legacyId = clean(r.id);
  if (!plate || !legacyId) return null;
  return {
    legacyId,
    customerLegacyId: clean(r.ugyfel_id)?.replace(/\.0$/, "") ?? null,
    plateNumber: plate.toUpperCase(),
    vin: clean(r.alvazszam)?.toUpperCase() ?? null,
    typeDescription: clean(r.tipus_szoveg),
    make: clean(r.marka),
    model: clean(r.modell_leiras),
    powerKw: int(r.teljesitmeny_kw),
    engineCode: clean(r.motorkod),
    tyreSize: clean(r.abroncs_meret),
  };
}

export interface ImportPlan {
  customers: CustomerInput[];
  vehicles: LegacyVehicle[];
  skipped: number;
  /** Vehicles that the previous program held several times (same plate and make, e.g. after a change of owner). */
  merged: number;
}

/** Placeholder VINs typed into the old program, such as 11111111111111222. */
const looksLikeDummyVin = (vin: string) => new Set(vin).size <= 4;
const usableVin = (vin: string | null | undefined) => !!vin && isValidVin(vin) && !looksLikeDummyVin(vin);

/**
 * The previous program often created the same car again when it got a new owner.
 * Rows with the same plate and make become one vehicle, unless both hold a real but
 * different VIN (then they are really two cars). The newest row (highest old id)
 * wins, so the current owner is kept; empty fields are filled from the older rows.
 */
export function mergeLegacyVehicles(vehicles: readonly LegacyVehicle[]): { vehicles: LegacyVehicle[]; merged: number } {
  const byId = (v: LegacyVehicle) => Number(v.legacyId) || 0;
  const sorted = [...vehicles].sort((a, b) => byId(b) - byId(a));
  const kept: LegacyVehicle[] = [];
  let merged = 0;
  for (const v of sorted) {
    const key = plateSearchKey(v.plateNumber);
    const make = (v.make ?? "").toUpperCase();
    const same = kept.find(
      (k) =>
        plateSearchKey(k.plateNumber) === key &&
        (k.make ?? "").toUpperCase() === make &&
        !(usableVin(k.vin) && usableVin(v.vin) && k.vin !== v.vin),
    );
    if (!same) {
      kept.push({ ...v });
      continue;
    }
    merged++;
    for (const f of ["typeDescription", "model", "powerKw", "engineCode", "tyreSize", "customerLegacyId"] as const) {
      if (same[f] == null || same[f] === "") (same as unknown as Record<string, unknown>)[f] = v[f];
    }
    if (!usableVin(same.vin) && usableVin(v.vin)) same.vin = v.vin;
    else if (!same.vin) same.vin = v.vin;
  }
  kept.sort((a, b) => byId(a) - byId(b));
  return { vehicles: kept, merged };
}

/** Builds the import plan; rows without the essentials are counted as skipped. */
export function planLegacyImport(customerRows: readonly Row[], vehicleRows: readonly Row[]): ImportPlan {
  let skipped = 0;
  const customers: CustomerInput[] = [];
  for (const r of customerRows) {
    const c = mapLegacyCustomer(r);
    if (c) customers.push(c);
    else skipped++;
  }
  const vehicles: LegacyVehicle[] = [];
  for (const r of vehicleRows) {
    const v = mapLegacyVehicle(r);
    if (v) vehicles.push(v);
    else skipped++;
  }
  const merged = mergeLegacyVehicles(vehicles);
  return { customers, vehicles: merged.vehicles, skipped, merged: merged.merged };
}
